import React, { useRef, useState } from 'react';
import { Button, Row, Col } from 'antd';

import {
  BOUserTypeEnum,
  ResourceDownloadResponse,
  FullBOUserResponseModel,
} from 'api/backofficeApiClient';
import { isEinOption } from 'utils/entityUtils';
import { checkIsCompanyOwner } from 'utils/userRolesUtils';
import BaseAlert from 'components/wrappers/BaseAlert';
import EditEntityInfoModal from './EditEntityInfoModal';
import { getSecureNumberValue } from 'utils/numberUtils';
import BasePageCard from 'components/wrappers/BasePageCard';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { getEntityInfoFile } from 'redux/reducers/accountSlice';
import { downloadFile, getFileNameWithoutExt } from 'utils/fileUtils';
import BaseSectionMessage from 'components/wrappers/BaseSectionMessage';
import { userSelector } from 'redux/selectors/authenticationSelectors';
import useScrollAfterRedirect from 'features/account/hooks/useScrollAfterRedirect';
import { LoadRecruiterDetailsOptions } from 'features/recruiter/RecruiterDetailsPage/types';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './EntityInfo.module.less';

type Props = {
  profile: FullBOUserResponseModel;
  entityInfoFile?: ResourceDownloadResponse;
  loadFullProfile: (options?: LoadRecruiterDetailsOptions) => Promise<void>;
};

export default function EntityInfo(props: Props) {
  const dispatch = useAppDispatch();
  const { profile, entityInfoFile, loadFullProfile } = props;
  const { boUserType, businessInfo, companyId } = profile;
  const user = useAppSelector(userSelector);
  const isCompanyOwner = checkIsCompanyOwner(user);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  useScrollAfterRedirect({ sectionRef });

  const handleOpenModal = () => {
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  const handleDownloadW8W9 = async () => {
    const result = await dispatch(getEntityInfoFile());

    if (getEntityInfoFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const onEditEntityInfo = async () => {
    await loadFullProfile({ shouldLoadEntityFile: true });
  };

  const taxIdLabel = isEinOption(businessInfo?.entityInformation?.numberType)
    ? 'EIN'
    : 'SSN';

  return (
    <>
      {isModalVisible && (
        <EditEntityInfoModal
          companyId={companyId}
          userType={boUserType}
          onEdit={onEditEntityInfo}
          onClose={handleCloseModal}
          entityInfoFile={entityInfoFile}
          entityInfo={businessInfo?.entityInformation}
        />
      )}

      <BasePageCard
        ref={sectionRef}
        title="Entity information"
        className="stretch-card"
        icons={<span className="icon icon-empire-state"></span>}
        subTitle={
          isCompanyOwner &&
          !!businessInfo?.entityInformation && (
            <Button
              onClick={handleOpenModal}
              className="with-icon mobile-icon-only"
              size="large"
              icon={<span className="icon icon-edit" />}
              data-id="openEntityInfoModal"
            >
              Edit
            </Button>
          )
        }
      >
        <div className="content-wrapper">
          {!businessInfo?.entityInformation && isCompanyOwner && (
            <BaseSectionMessage
              type="info"
              title="Info"
              message={
                <div className={styles.message}>
                  <span>Please update your entity information.</span>
                  <Button
                    className={styles.button}
                    onClick={handleOpenModal}
                    type="link"
                  >
                    Fill in now.
                  </Button>
                </div>
              }
            />
          )}

          {!!businessInfo?.entityInformation && (
            <Row gutter={[4, 4]} className="titled-sheet">
              {boUserType === BOUserTypeEnum.Company && (
                <>
                  {!!businessInfo.entityInformation.name && (
                    <Col xs={24} md={12}>
                      <div className="title">Legal company name:</div>
                      <div>{businessInfo.entityInformation.name}</div>
                    </Col>
                  )}

                  {!!businessInfo.entityInformation.number && (
                    <Col xs={24} md={12}>
                      <div className="title">EIN:</div>
                      <div>
                        {getSecureNumberValue(
                          businessInfo.entityInformation.number,
                          5,
                        )}
                      </div>
                    </Col>
                  )}
                </>
              )}

              {boUserType === BOUserTypeEnum.SelfEmployed && (
                <>
                  {!!businessInfo.entityInformation.name && (
                    <Col xs={24} md={12}>
                      <div className="title">Legal name:</div>
                      <div>{businessInfo.entityInformation.name}</div>
                    </Col>
                  )}

                  {!!businessInfo.entityInformation.number && (
                    <Col xs={24} md={12}>
                      <div className="title">{taxIdLabel}:</div>
                      <div>
                        {getSecureNumberValue(
                          businessInfo.entityInformation.number,
                          5,
                        )}
                      </div>
                    </Col>
                  )}
                </>
              )}

              {boUserType === BOUserTypeEnum.International && (
                <>
                  {!!businessInfo.entityInformation.name && (
                    <Col xs={24} md={12}>
                      <div className="title">Legal company name:</div>
                      <div>{businessInfo.entityInformation.name}</div>
                    </Col>
                  )}

                  {!!businessInfo.entityInformation.number && (
                    <Col xs={24} md={12}>
                      <div className="title">TIN:</div>
                      <div>
                        {getSecureNumberValue(
                          businessInfo.entityInformation.number,
                          5,
                        )}
                      </div>
                    </Col>
                  )}

                  {!!businessInfo.entityInformation.countryName && (
                    <Col xs={24} md={12}>
                      <div className="title">Country of incorporation:</div>
                      <div>{businessInfo.entityInformation.countryName}</div>
                    </Col>
                  )}
                </>
              )}

              {!!entityInfoFile && (
                <Col xs={24} md={12}>
                  <div className="title">
                    {boUserType === BOUserTypeEnum.International
                      ? 'Download W8:'
                      : 'Download W9:'}
                  </div>

                  <span className="link-imitation" onClick={handleDownloadW8W9}>
                    {getFileNameWithoutExt(entityInfoFile.fileName)}
                  </span>
                </Col>
              )}
            </Row>
          )}
        </div>
      </BasePageCard>
    </>
  );
}
